// Set a new password without the old one (the forgotten password's way back): offered for ten
// minutes after signing in with an emailed code, in that browser. Every other browser and iPad is
// signed out; this one stays. The server decides (set_password_after_code); this only offers it.
import { useState } from 'react';
import { RpcError } from '@flowology/client';
import { client, setCsrf } from '../api.ts';

const words = (e: unknown) => (e instanceof RpcError ? e.message : 'That didn’t work. Try again.');

export function SetPassword({
  until,
  minLength,
  hasPassword,
  timeZone,
  onChanged,
}: {
  /** When the offer ends (ten minutes after the emailed code). */
  until: string;
  minLength: number;
  hasPassword: boolean;
  timeZone: string;
  onChanged: () => Promise<void>;
}) {
  const [next, setNext] = useState('');
  const [again, setAgain] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const missing =
    next.length < minLength
      ? `It needs at least ${minLength} characters.`
      : again !== next
        ? 'Type the same password twice.'
        : null;
  const by = new Intl.DateTimeFormat('en-CA', { timeZone, timeStyle: 'short' }).format(new Date(until));
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await client.me<{ set: true; csrf: string }>('POST', '/me/password/set', { next });
      // Every session of theirs ended; this browser has a new one.
      setCsrf(r.csrf);
      setDone(true);
      setNext('');
      setAgain('');
      void onChanged();
    } catch (e) {
      setError(words(e));
    } finally {
      setBusy(false);
    }
  };
  if (done)
    return (
      <section className="sheet card signin-part" aria-label="Set a new password">
        <h2>Set a new password</h2>
        <p className="notice ok" role="status">
          Password set. Your other browsers and iPads were signed out.
        </p>
      </section>
    );
  return (
    <section className="sheet card signin-part" aria-label="Set a new password">
      <h2>Set a new password</h2>
      <p className="small muted">
        You signed in with an emailed code, so you can {hasPassword ? 'set a new password' : 'add a password'}{' '}
        without the old one until {by}. Your other browsers and iPads will be signed out.
      </p>
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      <label className="f">
        New password
        <input
          className="field"
          type="password"
          autoComplete="new-password"
          value={next}
          onChange={(e) => setNext(e.target.value)}
        />
      </label>
      <label className="f">
        The same again
        <input
          className="field"
          type="password"
          autoComplete="new-password"
          value={again}
          onChange={(e) => setAgain(e.target.value)}
        />
      </label>
      <div className="row">
        <button className="btn primary" disabled={busy || !!missing} onClick={() => void save()}>
          {busy ? 'Setting…' : 'Set password'}
        </button>
      </div>
      <p className="small muted" role="status" aria-live="polite">
        {missing && !busy ? missing : ''}
      </p>
    </section>
  );
}
